import React, { useState, useCallback } from 'react';
import { Input, AutoComplete, Spin, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { searchAssets } from '../services/api';

const { Text } = Typography;

// Helper: lấy array từ kết quả API (array trực tiếp hoặc object có data là array)
function getArrayFromApiResult(result: any): any[] {
  if (Array.isArray(result)) return result;
  if (result && typeof result === 'object' && Array.isArray(result.data)) return result.data;
  return [];
}

interface AssetOption {
  value: string;
  label: React.ReactNode;
  type: 'asset';
  id: number;
}

interface GlobalSearchProps {
  onAssetSelect?: (id: number) => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ onAssetSelect }) => {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<AssetOption[]>([]);

  // Debounce handleSearch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (value: string) => {
      if (!value) {
        setOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const assets = await searchAssets(value);
        const assetsArr = getArrayFromApiResult(assets);
        const opts = assetsArr.map((a: any) => ({
          value: `asset_${a.id}`,
          label: <span>{a.name} {a.asset_code && <Text type="secondary">({a.asset_code})</Text>}</span>,
          type: 'asset' as const,
          id: a.id,
        }));
        setOptions(opts);
      } catch (err) {
        setOptions([]);
      }
      setLoading(false);
    }, 300),
    []
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    debouncedSearch(value);
  };

  // Xử lý chọn kết quả
  const handleSelect = (value: string, option: any) => {
    if (option.type === 'asset' && onAssetSelect) onAssetSelect(option.id);
    setSearch('');
  };

  return (
    <AutoComplete
      style={{ width: 350 }}
      options={options}
      onSearch={handleSearch}
      onSelect={handleSelect}
      notFoundContent={loading ? <Spin size="small" /> : 'Không có kết quả'}
      value={search}
    >
      <Input
        size="large"
        placeholder="Tìm kiếm tài sản theo tên hoặc mã..."
        prefix={<SearchOutlined />}
        allowClear
      />
    </AutoComplete>
  );
};

export default GlobalSearch; 